import {Editor, Range, Point, Node, Transforms, Path, Element} from 'slate';

import {CustomEditor} from '../../pages/reportBuilder/constants/ReportBuilderInterfce';

export const PRESET_COLORS = [
  '#FF0000',
  '#FF9900',
  '#FFFF00',
  '#00FF00',
  '#00FFFF',
  '#0000FF',
  '#9900FF',
  '#FF00FF',
  '#E6B8AF',
  '#F4CCCC',
  '#FCE5CD',
  '#FFF2CC',
  '#D9EAD3',
  '#D0E0E3',
  '#C9DAF8',
  '#CFE2F3',
  '#D9D2E9',
  '#EAD1DC',
  '#DD7E6B',
  '#EA9999',
  '#F9CB9C',
  '#FFE599',
  '#B6D7A8',
  '#A2C4C9',
  '#A4C2F4',
  '#9FC5E8',
  '#B4A7D6',
  '#D5A6BD',
  '#CC4125',
  '#E06666',
  '#F6B26B',
  '#FFD966',
  '#93C47D',
  '#76A5AF',
  '#6D9EEB',
  '#6FA8DC',
  '#8E7CC3',
  '#C27BA0',
  '#FFFFFF',
  '#000000'
];

export const withPagination = (editor: Editor): Editor => {
  const {deleteBackward, deleteFragment} = editor;

  editor.deleteBackward = (unit) => {
    const {selection} = editor;
    if (selection && Range.isCollapsed(selection)) {
      const pageEntry = Editor.above(editor, {
        match: (n) =>
          !Editor.isEditor(n) && Element.isElement(n) && n.type === 'page'
      });

      if (pageEntry) {
        const [, pagePath] = pageEntry;
        const blockEntry = Editor.above(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            Editor.isBlock(editor, n) &&
            n.type !== 'page'
        });

        if (blockEntry) {
          const [, path] = blockEntry;
          const blockStart = Editor.start(editor, path);

          if (Point.equals(selection.anchor, blockStart)) {
            const index = path[path.length - 1];
            const page = Node.get(editor, pagePath) as any;
            const isFirstContentBlock =
              index === 0 ||
              (index === 1 && page.children[0].type === 'header-wrapper');

            if (isFirstContentBlock && pagePath[0] > 0) return;

            if (index > 0) {
              const prevSibling = page.children[index - 1];
              if (
                prevSibling?.type === 'header-wrapper' ||
                prevSibling?.type === 'footer-wrapper'
              )
                return;
            }
          }
        }
      }
    }
    deleteBackward(unit);
  };

  editor.deleteFragment = (direction) => {
    const {selection} = editor;
    if (selection) {
      const [start, end] = Range.edges(selection);
      const getLowestStructuralContainer = (point: Point) => {
        const [match] = Array.from(
          Editor.nodes(editor, {
            at: point.path,
            match: (n) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              ['header-wrapper', 'footer-wrapper', 'page'].includes(n.type),
            mode: 'lowest'
          })
        );

        return match;
      };

      const startContainer = getLowestStructuralContainer(start);
      const endContainer = getLowestStructuralContainer(end);
      if (
        startContainer &&
        endContainer &&
        startContainer[0] === endContainer[0] &&
        Path.equals(startContainer[1], endContainer[1])
      ) {
        deleteFragment(direction);

        return;
      }

      const isMultiPage = start.path[0] !== end.path[0];
      const nodes = Array.from(Editor.nodes(editor, {at: selection}));
      const hasStructural = nodes.some(
        ([n]) =>
          !Editor.isEditor(n) &&
          Element.isElement(n) &&
          ['page', 'header-wrapper', 'footer-wrapper'].includes(n.type)
      );

      if (isMultiPage || hasStructural) {
        const docStart = Editor.start(editor, []);
        const docEnd = Editor.end(editor, []);

        if (Point.equals(start, docStart) && Point.equals(end, docEnd)) {
          Editor.withoutNormalizing(editor, () => {
            editor.children = [
              {
                type: 'page',
                id: 'page-0',
                pageNumber: 0,
                children: [{type: 'paragraph', children: [{text: ''}]}]
              } as any
            ];
            editor.onChange();
          });

          return;
        }

        Editor.withoutNormalizing(editor, () => {
          Transforms.removeNodes(editor, {
            at: selection,
            match: (n) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              !['page', 'header-wrapper', 'footer-wrapper'].includes(n.type)
          });
        });

        return;
      }
    }
    deleteFragment(direction);
  };

  return editor;
};

export const sanitizeRange = (editor: Editor, range: Range): Range | null => {
  if (!range?.anchor || !range.focus) return null;
  try {
    if (
      !Editor.hasPath(editor, range.anchor.path) ||
      !Editor.hasPath(editor, range.focus.path)
    )
      return null;

    return {
      anchor: Editor.start(editor, range),
      focus: Editor.end(editor, range)
    };
  } catch {
    return null;
  }
};

export const setLineSpacing = (
  editor: Editor,
  spacing: number,
  selectedRanges: Range[] = []
): void => {
  const targetMatch = (n: Node) =>
    !Editor.isEditor(n) &&
    Element.isElement(n) &&
    !['page', 'header-wrapper', 'footer-wrapper'].includes((n as any).type) &&
    Editor.isBlock(editor, n);

  const ranges =
    selectedRanges.length > 0
      ? selectedRanges
      : editor.selection
        ? [editor.selection]
        : [];

  if (ranges.length === 0) return;

  Editor.withoutNormalizing(editor, () => {
    const allNodes = Array.from(
      Editor.nodes(editor, {
        at: [],
        match: targetMatch
      })
    );

    allNodes.forEach(([, path]) => {
      const nodeRange = Editor.range(editor, path);
      const isTargeted = ranges.some(
        (r) => Range.includes(r, path) || Range.intersection(r, nodeRange)
      );

      if (isTargeted) {
        Transforms.setNodes(editor, {lineHeight: spacing} as any, {at: path});
      }
    });
  });
};

export const withLineSpacing = (editor: Editor): Editor => {
  const {isInline} = editor;
  editor.isInline = (element: Element) =>
    (element as any).type === 'line-spacing' ? true : isInline(element);

  return editor;
};

export const LIST_TYPES = ['numbered-list', 'bulleted-list'];

export const withLists = (editor: Editor): Editor => {
  const {insertBreak, deleteBackward} = editor;

  editor.insertBreak = () => {
    const {selection} = editor;
    if (selection) {
      const [match] = Array.from(
        Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.type === 'list-item'
        })
      );
      if (match) {
        const [listItem] = match;
        if (Node.string(listItem).length === 0) {
          Transforms.unwrapNodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              LIST_TYPES.includes(n.type),
            split: true
          });
          Transforms.setNodes(editor, {type: 'paragraph'} as any);

          return;
        }
      }
    }
    insertBreak();
  };

  editor.deleteBackward = (unit) => {
    const {selection} = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [match] = Array.from(
        Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.type === 'list-item'
        })
      );
      if (match) {
        const [, listItemPath] = match;
        const start = Editor.start(editor, listItemPath);
        if (Range.equals(selection, {anchor: start, focus: start})) {
          Transforms.unwrapNodes(editor, {
            match: (n) =>
              !Editor.isEditor(n) &&
              Element.isElement(n) &&
              LIST_TYPES.includes(n.type),
            split: true
          });
          Transforms.setNodes(editor, {type: 'paragraph'} as any);

          return;
        }
      }
    }
    deleteBackward(unit);
  };

  return editor;
};

export const withColumns = (editor: Editor): CustomEditor => {
  const {normalizeNode, insertBreak, deleteBackward} = editor;

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;

    if (Element.isElement(node) && (node as any).type === 'columns-container') {
      // 1. Every child of columns-container must be a "column" node.
      //    Wrap any stray children in a column.
      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i] as any;
        if (!Element.isElement(child) || child.type !== 'column') {
          Transforms.wrapNodes(editor, {type: 'column', children: []} as any, {
            at: [...path, i]
          });

          return; // return early — normalizer will re-run
        }
      }

      // 2. columns-container must have at least one column child
      if (node.children.length === 0) {
        Transforms.insertNodes(
          editor,
          {
            type: 'column',
            children: [{type: 'paragraph', children: [{text: ''}]}]
          } as any,
          {at: [...path, 0]}
        );

        return;
      }

      // 3. Ensure a trailing paragraph after the container for cursor escape
      try {
        const parent = Node.parent(editor, path);
        const index = path[path.length - 1];
        const nextSibling = (parent.children as any)[index + 1];
        if (!nextSibling || nextSibling.type !== 'paragraph') {
          Transforms.insertNodes(
            editor,
            {type: 'paragraph', children: [{text: ''}]} as any,
            {at: Path.next(path)}
          );

          return;
        }
      } catch {
        // ignore — parent lookup may fail at root
      }
    }

    if (Element.isElement(node) && (node as any).type === 'column') {
      // 4. A column node must live inside a columns-container
      try {
        const parent = Node.parent(editor, path) as any;
        if (!Element.isElement(parent) || parent.type !== 'columns-container') {
          // Lift column children out and remove the orphan column wrapper
          Transforms.unwrapNodes(editor, {at: path});

          return;
        }
      } catch {
        // ignore
      }

      // 5. Every column must have at least one child (paragraph)
      if (node.children.length === 0) {
        Transforms.insertNodes(
          editor,
          {type: 'paragraph', children: [{text: ''}]} as any,
          {at: [...path, 0]}
        );

        return;
      }
    }

    normalizeNode(entry);
  };

  editor.insertBreak = () => {
    const {selection} = editor;
    if (selection) {
      // Check if we are inside a column
      const [columnEntry] = Array.from(
        Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            (n as any).type === 'column'
        })
      );

      if (columnEntry) {
        // Insert a paragraph inside the current column instead of
        // splitting the column or the container.
        Transforms.splitNodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            Editor.isBlock(editor, n) &&
            (n as any).type !== 'column' &&
            (n as any).type !== 'columns-container' &&
            (n as any).type !== 'page',
          always: true
        });

        return;
      }
    }
    insertBreak();
  };

  editor.deleteBackward = (unit) => {
    const {selection} = editor;
    if (selection && Range.isCollapsed(selection)) {
      // Check if cursor is inside a column
      const [columnEntry] = Array.from(
        Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            (n as any).type === 'column'
        })
      );

      if (columnEntry) {
        const [, columnPath] = columnEntry;
        const columnStart = Editor.start(editor, columnPath);

        // If cursor is at the very start of a column, prevent backspace
        // from merging into the previous column or breaking the container
        if (Point.equals(selection.anchor, columnStart)) {
          return;
        }
      }
    }
    deleteBackward(unit);
  };

  return editor;
};

export const withPageBreaks = (editor: Editor): Editor => {
  const {isVoid, normalizeNode} = editor;
  editor.isVoid = (element: Element) =>
    (element as any).type === 'page-break' ? true : isVoid(element);

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;
    if (Element.isElement(node) && node.type === 'page-break') {
      const parent = Node.parent(editor, path);
      const index = path[path.length - 1];
      const nextSibling = parent.children[index + 1] as any;
      if (!nextSibling || nextSibling.type !== 'paragraph') {
        Transforms.insertNodes(
          editor,
          {type: 'paragraph', children: [{text: ''}]} as any,
          {at: Path.next(path)}
        );

        return;
      }
    }
    normalizeNode(entry);
  };

  return editor;
};

export const withPageNumbers = (editor: Editor): Editor => {
  const {isInline} = editor;
  editor.isInline = (element: Element) =>
    (element as any).type === 'page-number' ? true : isInline(element);

  return editor;
};

export const withTables = (editor: Editor): Editor => {
  const {deleteBackward, deleteForward, insertBreak, normalizeNode} = editor;

  editor.normalizeNode = (entry) => {
    const [node, path] = entry;
    if (Element.isElement(node) && node.type === 'table') {
      const parent = Node.parent(editor, path);
      const index = path[path.length - 1];
      const nextSibling = parent.children[index + 1] as any;

      if (!nextSibling || nextSibling.type !== 'paragraph') {
        Transforms.insertNodes(
          editor,
          {type: 'paragraph', children: [{text: ''}]} as any,
          {at: Path.next(path)}
        );

        return;
      }
    }
    normalizeNode(entry);
  };

  editor.deleteBackward = (unit) => {
    const {selection} = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Array.from(
        Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.type === 'table-cell'
        })
      );

      if (cell) {
        const [, cellPath] = cell;
        const start = Editor.start(editor, cellPath);
        if (Point.equals(selection.anchor, start)) return;
      }
    }
    deleteBackward(unit);
  };

  editor.deleteForward = (unit) => {
    const {selection} = editor;
    if (selection && Range.isCollapsed(selection)) {
      const [cell] = Array.from(
        Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) &&
            Element.isElement(n) &&
            n.type === 'table-cell'
        })
      );

      if (cell) {
        const [, cellPath] = cell;
        const end = Editor.end(editor, cellPath);
        if (Point.equals(selection.anchor, end)) return;
      }
    }
    deleteForward(unit);
  };

  editor.insertBreak = () => {
    const {selection} = editor;
    if (selection) {
      const [table] = Array.from(
        Editor.nodes(editor, {
          match: (n) =>
            !Editor.isEditor(n) && Element.isElement(n) && n.type === 'table'
        })
      );
      if (table) return;
    }
    insertBreak();
  };

  return editor;
};

export const withImages = (editor: Editor): Editor => {
  const {isVoid, isInline} = editor;
  editor.isInline = (element) =>
    (element.type === 'image' ||
      element.type === 'shape' ||
      element.type === 'placeholder-image') &&
    element.float === 'inline'
      ? true
      : isInline(element);
  editor.isVoid = (element: Element) =>
    [
      'image',
      'placeholder-image',
      'shape',
      'framework-question',
      'spacer',
      'page-break',
      'page-number'
    ].includes((element as any).type as string)
      ? true
      : isVoid(element);

  return editor;
};

export const withLinks = (editor: Editor): Editor => {
  const {isInline} = editor;
  editor.isInline = (element: Element) =>
    (element as any).type === 'link' ? true : isInline(element);

  return editor;
};
